import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { User, Mail, Lock, Camera, Save, ArrowLeft, Eye, EyeOff, Shield } from 'lucide-react';
import Layout from '../components/Layout';
import AvatarEditor from '../components/AvatarEditor';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { updateProfile, reset, updateUserAvatar } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import { usersAPI } from '../utils/api';

// Simplified profile schema - removed password fields
const profileSchema = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters'),
  email: yup.string().required('Email is required').email('Please enter a valid email'),
  companyName: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  currentPassword: yup.string(),
});

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isLoading, isError, isSuccess, message } = useSelector(state => state.auth);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
    watch,
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      companyName: user?.companyName || '',
      currentPassword: '',
    },
  });

  const watchEmail = watch('email');

  useEffect(() => {
    if (user) {
      resetForm({
        name: user.name,
        email: user.email,
        companyName: user.companyName,
        currentPassword: '',
      });
      setOriginalEmail(user.email);
    }
  }, [user, resetForm]);

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }
    if (isSuccess && message) {
      toast.success(message);
      // Reset current password field after successful update
      resetForm(prev => ({
        ...prev,
        currentPassword: '',
      }));
    }
    dispatch(reset());
  }, [isError, isSuccess, message, dispatch, resetForm]);

  const onSubmit = data => {
    // Check if email has changed
    const isEmailChanged = data.email !== originalEmail;

    // Validation: require current password ONLY if email is being changed
    if (isEmailChanged && !data.currentPassword) {
      toast.error('Current password is required when changing email address');
      return;
    }

    // Build update data - always include basic fields
    const updateData = {
      name: data.name,
      email: data.email,
      companyName: data.companyName,
    };

    // Only include current password if email is being changed
    if (isEmailChanged) {
      updateData.currentPassword = data.currentPassword;
    }

    dispatch(updateProfile(updateData));
  };

  const handleAvatarChange = e => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setSelectedImageFile(file);
      setShowAvatarEditor(true);
    }
  };

  const handleAvatarSave = async blob => {
    try {
      setIsUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', blob, 'avatar.png');

      const response = await usersAPI.uploadAvatar(formData);

      // S3 URL is already complete, no need to modify
      const avatarUrl = response.data.avatarUrl;

      console.log('Avatar uploaded successfully:', avatarUrl);

      // Update preview immediately
      setAvatarPreview(avatarUrl);

      // Update Redux state with new avatar URL
      dispatch(updateUserAvatar(avatarUrl));

      toast.success('Avatar updated successfully!');
      setShowAvatarEditor(false);
      setSelectedImageFile(null);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(error.response?.data?.message || 'Failed to upload avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarCancel = () => {
    setShowAvatarEditor(false);
    setSelectedImageFile(null);
  };

  const handlePasswordModalOpen = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordModal(false);
  };

  const getUserInitials = name => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Layout>
      {showAvatarEditor && selectedImageFile && (
        <AvatarEditor
          imageFile={selectedImageFile}
          onSave={handleAvatarSave}
          onCancel={handleAvatarCancel}
        />
      )}

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={handlePasswordModalClose}
        onSuccess={handlePasswordChangeSuccess}
      />

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="mt-2 text-gray-600">
            Manage your personal account information and security settings
          </p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <Lock className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Profile Update Information</h3>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• You can update your name and company without a password</li>
                  <li>• Changing your email address requires your current password for security</li>
                  <li>
                    • To change your password, use the "Change Password" button in the Security
                    section below
                  </li>
                  {(user?.role === 'super-admin' || user?.role === 'admin') && (
                    <li>• To manage other users, please use the Users section</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-8">
            <div className="flex items-center">
              <div className="relative">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                  {avatarPreview || user?.avatarUrl ? (
                    <img
                      src={avatarPreview || user?.avatarUrl}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      onError={e => {
                        console.error('Avatar load error:', e.target.src);
                        console.error('Avatar URL:', avatarPreview || user?.avatarUrl);
                        e.target.style.display = 'none';
                      }}
                      crossOrigin="anonymous"
                    />
                  ) : (
                    getUserInitials(user?.name)
                  )}
                </div>
                <label
                  htmlFor="avatar-upload"
                  className={`absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-md cursor-pointer hover:bg-gray-50 transition-colors ${
                    isUploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUploadingAvatar ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  ) : (
                    <Camera className="h-4 w-4 text-gray-600" />
                  )}
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  disabled={isUploadingAvatar}
                />
              </div>
              <div className="ml-6">
                <h2 className="text-2xl font-bold text-white">{user?.name}</h2>
                <p className="text-blue-100">{user?.email}</p>
                <p className="text-blue-100 text-sm">{user?.companyName}</p>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="h-4 w-4 inline mr-2" />
                    Full Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    {...register('name')}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="h-4 w-4 inline mr-2" />
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register('email')}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="companyName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <User className="h-4 w-4 inline mr-2" />
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    {...register('companyName')}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.companyName ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.companyName && (
                    <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Change Security Verification */}
            {watchEmail !== originalEmail && (
              <div className="border-t pt-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <Lock className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">
                        Security Verification Required
                      </h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        You are changing your email address. Please enter your current password to
                        confirm this change.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="currentPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Current Password *
                  </label>
                  <div className="relative">
                    <input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      {...register('currentPassword')}
                      className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.currentPassword ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.currentPassword.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Security Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                <Shield className="h-5 w-5 inline mr-2" />
                Security
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Password</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Change your password to keep your account secure
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handlePasswordModalOpen}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors duration-200 flex items-center"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default UserProfile;
